// src/components/FounderBadge.js
// ============================================================================
// FOUNDER BADGE — the earned profile aura (first 1,000 members).
//
// A steady electric-blue glow ring hugs the avatar; the gold King Crit die
// (20_dice.webp) orbits it COUNTER-CLOCKWISE while spinning slowly clockwise
// on its own axis, trailing a soft gold afterglow "wake" of fading ghosts.
// The orbiting die is the only motion — the glow itself does NOT pulse
// (founder direction 2026-07-07).
//
// Web-only visual (CSS keyframes, same injection pattern as TsunamiMeter's
// Rumble burst). On native it renders the avatar unchanged. Wrap any avatar:
//   <FounderBadge size={72} active={profileBadge === 'founder'}>{avatarNode}</FounderBadge>
// ============================================================================

import React from 'react';
import { View, Image, Platform } from 'react-native';

const IS_WEB = Platform.OS === 'web';
const DIE = require('../../assets/20_dice.webp'); // gold King Crit d20

let _injected = false;
function injectStyles() {
  if (_injected || !IS_WEB || typeof document === 'undefined') return;
  _injected = true;
  const css = `
  @keyframes ccFbOrbit { to { transform: rotate(-360deg); } } /* orbit: counter-clockwise */
  @keyframes ccFbSpin  { to { transform: rotate(360deg); } }  /* die self-spin: clockwise */
  .cc-fb-layer { animation: ccFbOrbit 5s linear infinite; }
  .cc-fb-die   { animation: ccFbSpin 3.4s linear infinite; }
  @media (prefers-reduced-motion: reduce) {
    .cc-fb-layer { animation: none; }
    .cc-fb-die   { animation: none; }
    .cc-fb-ghost { display: none; }
  }`;
  const el = document.createElement('style');
  el.textContent = css;
  document.head.appendChild(el);
}

// Static angular offsets place each ghost CLOCKWISE of the die, i.e. BEHIND it
// along the counter-clockwise orbit — so the wake trails, never leads.
const GHOSTS = [
  { off: 7, op: 0.40 },
  { off: 14, op: 0.26 },
  { off: 21, op: 0.15 },
  { off: 28, op: 0.07 },
];

const BLUE = 'rgba(0,207,255,';

// `size`  = avatar diameter (drives all orbit geometry).
// `box`   = layout footprint. Defaults to the full orbit field (profile use).
//           Pass the avatar size for compact spots (header/leaderboard rows) so
//           the badge occupies only the avatar's space and the orbit overflows
//           instead of reflowing the row.
export default function FounderBadge({ size = 72, box = null, active = true, children }) {
  if (!IS_WEB || !active) return <View>{children}</View>;
  injectStyles();

  const k = size / 90;
  const stage = 190 * k;                       // orbit field diameter
  const footprint = Math.round(box != null ? box : stage);
  const c = footprint / 2;                      // everything centers here

  const haloD = 122 * k;                        // stage - 2*34
  const ringD = 98 * k;                         // stage - 2*46
  const dieSize = Math.round(42 * k);
  const ghostSize = Math.round(34 * k);
  const radius = 72 * k;                        // die orbit radius

  // A full-stage layer centered on the footprint. Rotating it about its own
  // center orbits its child around the footprint center.
  const layer = { position: 'absolute', left: c - stage / 2, top: c - stage / 2, width: stage, height: stage };
  const circle = (d) => ({ position: 'absolute', left: c - d / 2, top: c - d / 2, width: d, height: d, borderRadius: '50%' });
  // child sits at top-center of a layer, pushed out to the orbit radius
  const atRadius = (cs) => ({ position: 'absolute', left: stage / 2 - cs / 2, top: stage / 2 - radius - cs / 2, width: cs, height: cs });

  return (
    <div style={{ position: 'relative', width: footprint, height: footprint, overflow: 'visible', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {/* steady glow — halo + ring (no pulse) */}
      <div style={{
        ...circle(haloD), zIndex: 1,
        background: `radial-gradient(circle, ${BLUE}0.45) 0%, rgba(0,128,255,0.18) 45%, transparent 70%)`,
        filter: 'blur(8px)',
      }} />
      <div style={{
        ...circle(ringD), zIndex: 2, border: `2px solid ${BLUE}0.8)`,
        boxShadow: `0 0 ${14 * k}px ${BLUE}0.7), 0 0 ${32 * k}px rgba(0,140,255,0.4), inset 0 0 ${12 * k}px ${BLUE}0.45)`,
      }} />

      {/* avatar */}
      <div style={{ position: 'relative', zIndex: 3 }}>{children}</div>

      {/* afterglow wake — ghosts share the CCW orbit, held behind the die by a static offset */}
      {GHOSTS.map((g, i) => (
        <div key={i} style={{ ...layer, zIndex: 4, transform: `rotate(${g.off}deg)` }}>
          <div className="cc-fb-layer" style={{ position: 'absolute', inset: 0 }}>
            <div className="cc-fb-ghost" style={{
              ...atRadius(ghostSize), borderRadius: '50%',
              background: `radial-gradient(circle, rgba(255,207,51,${g.op}) 0%, rgba(224,168,0,${g.op * 0.6}) 40%, transparent 70%)`,
              filter: 'blur(4px)',
            }} />
          </div>
        </div>
      ))}

      {/* the die — leads the wake, orbits CCW, spins CW on its own axis */}
      <div style={{ ...layer, zIndex: 5 }}>
        <div className="cc-fb-layer" style={{ position: 'absolute', inset: 0 }}>
          <div style={{
            ...atRadius(dieSize),
            filter: 'drop-shadow(0 0 5px rgba(0,207,255,0.85)) drop-shadow(0 0 13px rgba(0,140,255,0.5))',
          }}>
            <div className="cc-fb-die" style={{ width: dieSize, height: dieSize }}>
              <Image source={DIE} style={{ width: dieSize, height: dieSize }} resizeMode="contain" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
